/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useRef, useEffect } from 'react';
import { useIndicatorMetadata } from '../../../context/IndicatorMetadataContext';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem } from '../../ui/select';
import { TIMEFRAMES } from '../../../lib/timeframes';

const IndicatorSelector = ({ value, onChange, dense = false, baseTimeframe }) => {
  const { indicatorMetadata, isLoading, error } = useIndicatorMetadata();
  // Always declare hooks before any early returns
  const lastLenRef = useRef(value?.params?.length);
  useEffect(() => {
    if (import.meta.env?.DEV) {
      if (lastLenRef.current !== value?.params?.length) {
        console.log('[IndicatorSelector] lengthChange', {
          indicator: value?.name,
          from: lastLenRef.current,
          to: value?.params?.length,
          params: value?.params,
        });
        lastLenRef.current = value?.params?.length;
      }
    }
  }, [value?.params?.length, value?.name, value?.params]);

  if (isLoading) return <p>Loading indicators...</p>;
  if (error) return <p>Error loading indicators: {error.message}</p>;

  const canonicalizeParamName = (name) => name?.toLowerCase() === 'length' ? 'length' : name;
  const handleIndicatorChange = (indicatorName) => {
    const selectedIndicator = indicatorMetadata.find(ind => ind.name === indicatorName);
    if (!selectedIndicator) return;

    // Preserve existing param values when possible; only fill missing keys with defaults.
    const newParams = { ...(value?.params || {}) };
    selectedIndicator.parameters.forEach(param => {
      const key = canonicalizeParamName(param.name);
      // migrate old differently-cased key (e.g., Length) if present
      Object.keys(newParams).forEach(existing => {
        if (canonicalizeParamName(existing) === key && existing !== key) {
          newParams[key] = newParams[existing];
          delete newParams[existing];
        }
      });
      if (newParams[key] === undefined || newParams[key] === null) {
        newParams[key] = param.default_value;
      }
    });
    // Drop params that are no longer valid for the new indicator
    Object.keys(newParams).forEach(k => {
      if (!selectedIndicator.parameters.find(p => canonicalizeParamName(p.name) === canonicalizeParamName(k))) delete newParams[k];
    });

    const next = {
      type: 'indicator',
      name: indicatorName,
      output: selectedIndicator.outputs?.[0] || value?.output,
      params: newParams,
      input: value?.input || { type: 'price', source: 'close' },
      timeframe: value?.timeframe, // preserve if set
    };
  if (import.meta.env?.DEV) {
      console.log('[IndicatorSelector] indicatorChange', { indicatorName, params: next.params });
    }
    onChange(next);
  };

  const handleOutputChange = (output) => {
    onChange({
      ...value,
      output,
    });
  };

  const handleParamChange = (paramName, paramValue) => {
    const canonical = canonicalizeParamName(paramName);
    const next = {
      ...value,
      params: {
        ...value.params,
        [canonical]: paramValue,
      },
    };
  if (import.meta.env?.DEV) {
      console.log('[IndicatorSelector] paramChange', { indicator: value?.name, paramName: canonical, paramValue });
    }
    onChange(next);
  };

  const selectedIndicator = indicatorMetadata.find(
    ind => ind.name?.toLowerCase() === (value?.name || '').toLowerCase()
  );

  return (
    <div className={(dense ? 'space-y-1' : 'space-y-2') + ' max-w-full'}>
      <Select
        onValueChange={handleIndicatorChange}
        value={selectedIndicator?.name || ''}
        placeholder="Select an indicator"
        searchable
        searchPlaceholder="Search indicators…"
        compact={true}
        className="w-[220px] md:w-[260px]"
        itemHeight={30}
        maxDropdownHeight={180}
      >
        <SelectContent>
          {indicatorMetadata.map(indicator => (
            <SelectItem key={indicator.name} value={indicator.name}>
              {indicator.display_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selectedIndicator && (
        <div className={(dense ? 'space-y-1 pt-1' : 'space-y-2 pt-2') + ' max-w-full'}>
          {/* Timeframe */}
          <div className="grid grid-cols-[1fr_auto] items-center gap-2">
            <label className={(dense ? 'text-xs' : 'text-sm') + ' pr-2'}>Timeframe</label>
            <select
              className={(dense ? 'text-xs h-7' : 'h-8') + ' bg-backgroundSurface text-textPrimary border border-borderDefault rounded w-[160px]'}
              value={value?.timeframe || ''}
              onChange={(e) => onChange({ ...value, timeframe: e.target.value || undefined })}
            >
              <option value="">Base ({baseTimeframe || 'M1'})</option>
              {TIMEFRAMES.map(tf => (
                <option key={tf} value={tf}>{tf}</option>
              ))}
            </select>
          </div>
          {/* Lag (bars) - optional */}
          <div className="grid grid-cols-[1fr_auto] items-center gap-2">
            <label className={(dense ? 'text-xs' : 'text-sm') + ' pr-2'}>Lag (bars)</label>
            <input
              type="number"
              min={0}
              step={1}
              className={(dense ? 'text-xs h-7 w-20' : 'h-8 w-24') + ' bg-backgroundSurface text-textPrimary border border-borderDefault rounded px-2'}
              value={Number.isFinite(value?.lag) ? value.lag : 0}
              onChange={(e) => {
                const n = Math.max(0, parseInt(e.target.value || '0', 10));
                onChange({ ...value, lag: n });
              }}
              placeholder="0"
            />
          </div>
          {/* Input source picker (price vs nested indicator) */}
          <div className="grid grid-cols-[1fr_auto] items-center gap-2">
            <label className={(dense ? 'text-xs' : 'text-sm') + ' pr-2'}>Input Source</label>
            <div className="flex items-center gap-2">
              <select
                className={(dense ? 'text-xs h-7' : 'h-8') + ' bg-backgroundSurface text-textPrimary border border-borderDefault rounded w-[120px]'}
                value={value?.input?.type === 'indicator' ? 'indicator' : 'price'}
                onChange={(e) => {
                  const t = e.target.value;
                  if (t === 'price') {
                    onChange({ ...value, input: { type: 'price', source: 'close', timeframe: undefined } });
                  } else {
                    onChange({ ...value, input: { type: 'indicator', name: 'EMAIndicator', params: { length: 21, src: 'close' }, output: 'ema' } });
                  }
                }}
              >
                <option value="price">Price</option>
                <option value="indicator">Indicator</option>
              </select>
              {value?.input?.type === 'price' && (
                <select
                  className={(dense ? 'text-xs h-7' : 'h-8') + ' bg-backgroundSurface text-textPrimary border border-borderDefault rounded w-[120px]'}
                  value={value?.input?.source || 'close'}
                  onChange={(e) => onChange({ ...value, input: { type: 'price', source: e.target.value } })}
                >
                  {['open','high','low','close','tick'].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
          {value?.input?.type === 'price' && (
            <div className="grid grid-cols-[1fr_auto] items-center gap-2">
              <label className={(dense ? 'text-xs' : 'text-sm') + ' pr-2'}>Input TF</label>
              <select
                className={(dense ? 'text-xs h-7' : 'h-8') + ' bg-backgroundSurface text-textPrimary border border-borderDefault rounded w-[160px]'}
                value={value?.input?.timeframe || ''}
                onChange={(e) => onChange({ ...value, input: { ...value.input, timeframe: e.target.value || undefined } })}
              >
                <option value="">Same as node ({value?.timeframe || baseTimeframe || 'M1'})</option>
                {TIMEFRAMES.map(tf => (
                  <option key={tf} value={tf}>{tf}</option>
                ))}
              </select>
            </div>
          )}
          {value?.input?.type === 'indicator' && (
            <div className="mt-1">
              <div className={(dense ? 'text-xs' : 'text-sm') + ' mb-1 text-textSecondary'}>Nested Input</div>
              <IndicatorSelector
                dense
                value={value.input}
                onChange={(next) => onChange({ ...value, input: next })}
                baseTimeframe={value?.timeframe || baseTimeframe}
              />
            </div>
          )}

  {selectedIndicator.outputs && selectedIndicator.outputs.length > 0 && (
    <div className="grid grid-cols-[1fr_auto] items-center gap-2">
              <label className={(dense ? 'text-xs' : 'text-sm') + ' pr-2'}>Output</label>
              <Select
                onValueChange={handleOutputChange}
                value={value.output || selectedIndicator.outputs[0]}
                placeholder="Select an output"
                compact={true}
        className="w-[200px]"
                itemHeight={30}
                maxDropdownHeight={180}
              >
                <SelectContent>
                  {selectedIndicator.outputs.map(output => (
                    <SelectItem key={output} value={output}>
                      {output}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {selectedIndicator.parameters
            .slice()
            .sort((a, b) => ((a?.x_order ?? Number.POSITIVE_INFINITY) - (b?.x_order ?? Number.POSITIVE_INFINITY)))
            .map(param => (
            <div key={param.name} className="grid grid-cols-[1fr_auto] items-center gap-2">
              <label className={(dense ? 'text-xs' : 'text-sm') + ' pr-2'}>{param.display_name}</label>
              {renderParamInput(param, value.params[canonicalizeParamName(param.name)], handleParamChange, dense)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const renderParamInput = (param, currentValue, onChange, dense = false) => {
  const { name, parameter_type, options, min_value, max_value, step } = param;

  // Normalize backend types to a small set we support
  const normType = (() => {
    const t = (parameter_type || '').toString().toLowerCase();
    if (t === 'int' || t === 'integer') return 'integer';
    if (t === 'float' || t === 'number' || t === 'double' || t === 'decimal') return 'float';
    if (t === 'bool' || t === 'boolean') return 'boolean';
    if (t === 'enum' || t === 'select' || t === 'options') return 'enum';
    if (t === 'str' || t === 'string' || t === 'text') return 'string';
    return t; // fallback to original
  })();

  const inputClass = dense ? 'w-24 h-7 text-xs' : 'w-28';
  const selectClass = dense ? 'w-[140px] text-xs' : 'w-[180px]';

  switch (normType) {
    case 'integer':
    case 'float':
      return (
        <Input
          type="number"
          value={currentValue ?? ''}
          onChange={(e) => onChange(name, normType === 'integer' ? parseInt(e.target.value, 10) : parseFloat(e.target.value))}
          min={min_value}
          max={max_value}
          // If float/number, allow any by default; for integer default to 1
          step={step ?? (normType === 'integer' ? 1 : 'any')}
          className={inputClass}
        />
      );
    case 'enum':
    case 'string': {
      // If options present, show a dropdown; else free text
      const normOptions = Array.isArray(options)
        ? options.map((opt) => (typeof opt === 'object' && opt !== null ? opt : { value: String(opt), label: String(opt) }))
        : [];
      if (normOptions.length > 0) {
        return (
          <Select
            onValueChange={(val) => onChange(name, val)}
            value={currentValue ?? ''}
            placeholder="Select..."
            className={selectClass}
            searchable
            searchPlaceholder={`Search ${name}…`}
          >
            <SelectContent>
              {normOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
      return <Input type="text" value={currentValue ?? ''} onChange={(e) => onChange(name, e.target.value)} className={dense ? 'h-7 text-xs w-28' : undefined} />;
    }
    case 'boolean':
      return (
        <input
          type="checkbox"
          checked={!!currentValue}
          onChange={(e) => onChange(name, e.target.checked)}
          className="form-checkbox h-5 w-5"
        />
      );
    default:
      return <p>Unsupported param type</p>;
  }
};

export default IndicatorSelector;
